import * as THREE from "three";
import { GameState } from "@/GameState";
import type { GUIControl, GUIListBox, GUIScrollBar } from "@/gui";
import { Utility } from "@/utility/Utility";
import { EngineMode } from "@/enums/engine/EngineMode";
import { EngineState } from "@/enums/engine/EngineState";
import { KeyMapAction } from "@/enums/controls/KeyMapAction";
import { MiniGameType } from "@/enums/engine/MiniGameType";
import { FollowerCamera } from "@/engine/FollowerCamera";
import { BitWise } from "@/utility/BitWise";
import { ModuleObjectType } from "@/enums/module/ModuleObjectType";
import { GUIControlTypeMask } from "@/enums/gui/GUIControlTypeMask";
import { GUIControlEventFactory } from "@/gui/GUIControlEventFactory";
import { Keyboard } from "@/controls/Keyboard";
import { GamePad } from "@/controls/GamePad";
import { Mouse } from "@/controls/Mouse";
import { KeyMapper } from "@/controls/KeyMapper";
import { AnalogInput } from "@/controls/AnalogInput";
import { TGAObject } from "@/resource/TGAObject";
import { GameFileSystem } from "@/utility/GameFileSystem";
import { TURN_SPEED_FAST } from "@/engine/TurnSpeeds";
import { EventManager } from "@/managers/EventManager";

/**
 * IngameControls class.
 * 
 * KotOR JS - A remake of the Odyssey Game Engine that powered KotOR I & II
 * 
 * @file IngameControls.ts
 * @author KobaltBlu <https://github.com/KobaltBlu>
 * @license {@link https://www.gnu.org/licenses/gpl-3.0.txt|GPLv3}
 */
export class IngameControls {
  Mouse: Mouse = Mouse;
  KeyMapper: KeyMapper = KeyMapper;

  camera: THREE.Camera;
  element: HTMLElement;

  //controllers
  keyboard: Keyboard = new Keyboard();
  gamePad: GamePad;

  gamePadMovement: boolean = false;

  plMoveEvent: (e: any) => void;

  constructor(camera: THREE.Camera, element: HTMLElement){

    this.camera = camera;
    this.element = element;

    this.gamePad = new GamePad();

    this.initKeys();

    this.element.requestPointerLock = this.element.requestPointerLock;

    window.addEventListener('keydown', (e: KeyboardEvent) => {
      this.keyboard.onKeyDown(e);

      if(GameState.MenuManager.activeGUIElement){
        if(typeof GameState.MenuManager.activeGUIElement.onKeyDown === 'function'){
          GameState.MenuManager.activeGUIElement.onKeyDown(e);
        }
      }

      const ae = GameState.MenuManager.activeGUIElement as { editable?: boolean } | undefined;
      const editingTextField = !!(ae && ae.editable === true);

      if(
        !editingTextField &&
        (GameState.Mode == EngineMode.GUI || GameState.Mode == EngineMode.DIALOG) &&
        (e.code === 'ArrowUp' || e.code === 'ArrowDown' || e.code === 'UpArrow' || e.code === 'DownArrow')
      ){
        const fg = GameState.MenuManager.GetForegroundMenu();
        if(fg){
          if(e.code === 'ArrowUp' || e.code === 'UpArrow'){
            fg.triggerControllerDUpPress();
          }else{
            fg.triggerControllerDDownPress();
          }
          e.preventDefault();
        }
      }

      if(GameState.MenuManager.activeMenus.length){
        GameState.MenuManager.activeMenus[GameState.MenuManager.activeMenus.length-1].triggerEventListener('keydown', e);
      }
    });

    window.addEventListener('keyup', (e: KeyboardEvent) => {
      /**
       * Debugger Hotkey
       */
      if(e.key == 'D' && e.ctrlKey && e.shiftKey){
        GameState.Debugger.open();
      }

      /**
       * Generate a TGA Screenshot of the Game canvas and export it to the game directory
       */
      if(e.key == 'PrintScreen'){
        if(GameState.iniConfig.getProperty('Game Options.EnableScreenShot') != 1){
          return;
        }
        /**
         * Render the scene
         */
        const oldCursorState = GameState.scene_cursor_holder.visible;
        GameState.scene_cursor_holder.visible = false;
        GameState.renderer.clear();
        GameState.composer.render(0);
        GameState.scene_cursor_holder.visible = oldCursorState;

        /**
         * Build DOM Image Element
         */
        const screenshot = new Image();
        screenshot.src = GameState.canvas.toDataURL('image/png');
        screenshot.onload = async function() {
          /**
           * Draw the contents of the Image onto an OffscreenCanvas
           */
          const width = GameState.ResolutionManager.getViewportWidth();
          const height = GameState.ResolutionManager.getViewportHeight();
          const ssCanvas = new OffscreenCanvas(width, height);
          const ctx = ssCanvas.getContext('2d');
          ctx.drawImage(screenshot, 0, 0, width, height);

          /**
           * Build a TGAObject from the canvas element
           */
          const tga = TGAObject.FromCanvas(ssCanvas);

          /**
           * Generate the export filename
           */
          const count = (await GameFileSystem.readdir('')).filter( (file) => {
            return !!file.match(/KotOR\d{4}.tga/);
          }).length;
          const isK1 = GameState.GameKey == 'KOTOR';
          const ssName = isK1 ? 'KotOR' : 'K2_';
          const ssNumber = "00000" + count;
          const ssMaxDigits = isK1 ? 4 : 5;
          const ssFilename = `${ssName}${ssNumber.substring(ssNumber.length-ssMaxDigits)}.tga`;

          /**
           * Export the generated TGAObject as a TGA Image
           */
          GameFileSystem.writeFile(ssFilename, await tga.toExportBuffer());
        };
      }

      this.keyboard.onKeyUp(e);

      if(GameState.MenuManager.activeGUIElement){
        if(typeof GameState.MenuManager.activeGUIElement.onKeyUp === 'function'){
          GameState.MenuManager.activeGUIElement.onKeyUp(e);
        }
      }

      if(GameState.MenuManager.activeMenus.length){
        GameState.MenuManager.activeMenus[GameState.MenuManager.activeMenus.length-1].triggerEventListener('keyup', e);
      }
    });

    // Ask the browser to release the pointer
    document.exitPointerLock = document.exitPointerLock;
    document.addEventListener('pointerlockchange', this.plChangeCallback.bind(this), true);

    window.addEventListener('mousedown', (event: MouseEvent) => {
      // Ignore clicks on React/chrome overlays that sit above the game canvas.
      if(!this.isGameCanvasMouseTarget(event)){
        return;
      }

      // Block GUI selection while a movie is playing
      if(GameState.VideoManager.isMoviePlaying()){
        return;
      }

      if(!Mouse.pointerLock){
        Mouse.Update(event.clientX, event.clientY);
      }
      GameState.MenuManager.activeGUIElement = undefined;

      if(event.button == 0){

      }else if(event.button == 2){
        // Ask the browser to lock the pointer
        // this.element.requestPointerLock();
      }

      GameState.raycaster.setFromCamera( GameState.mouse, GameState.camera_gui );
      
      let clickCaptured = false;

      const customEvent = GUIControlEventFactory.generateEventObject();

      Mouse.downItem = null;
      Mouse.clickItem = null;

      // Block GUI selection while a movie is playing
      if(!GameState.VideoManager.isMoviePlaying() && event.button == 0){
        const uiControls = this.MenuGetActiveUIElements();
        for(let i = 0; i < uiControls.length; i++){
          if(!customEvent.propagate)
            break;
          
          const control = uiControls[i];
          if(!(control.widget.parent instanceof THREE.Scene) && control.widget.visible){
            clickCaptured = true;
            if(GameState.debug.CONTROLS)
              console.log('uiControls', control)
            try{
              if(control.processEventListener('mouseDown', [customEvent])){
                Mouse.downItem = control;
                customEvent.propagate = false;
              }

              if(control.eventListeners['click'].length){
                Mouse.clickItem = control;
                customEvent.propagate = false;
              }
            }catch(e){

            }
          }
        }
      }

      if(event.button == 0){
        Mouse.leftDown = true;
      }else if(event.button == 1){
        Mouse.rightDown = true;
      }
    });

    window.addEventListener('mousemove', (event: MouseEvent) => {
      // Allow look while pointer-locked; otherwise only track when over the canvas
      // (or while a canvas-started drag is held).
      if(!this.isGameCanvasMouseTarget(event) && !Mouse.leftDown && !Mouse.rightDown){
        return;
      }

      // Block GUI selection while a movie is playing
      if(GameState.VideoManager.isMoviePlaying()){
        return;
      }

      GameState.scene_cursor_holder.visible = true;

      if(Mouse.pointerLock){
        Mouse.Update( Mouse.positionWindow.x + event.movementX, Mouse.positionWindow.y + event.movementY );
      }else{
        Mouse.Update( event.clientX, event.clientY );
      }
      
      if(Mouse.downItem && Mouse.leftDown){
        if(BitWise.InstanceOf(Mouse.downItem?.objectType, GUIControlTypeMask.GUIControl)){
          if(!(Mouse.downItem.widget.parent instanceof THREE.Scene)){
            Mouse.downItem.processEventListener('mouseMove', [])
          }
        }
      }
      if(event.movementX || event.movementY){
        Mouse.MouseX += event.movementX;
        Mouse.MouseY += event.movementY;
      }else{
        Mouse.MouseX = event.pageX - this.element.offsetLeft;
        Mouse.MouseY = event.pageY - this.element.offsetTop;
      }
    });

    window.addEventListener('mouseup', (event: MouseEvent) => {
      // Finish a canvas-started press even if release lands on overlay chrome;
      // otherwise ignore presses that never hit the canvas.
      if(!this.isGameCanvasMouseTarget(event) && !Mouse.leftDown && !Mouse.rightDown){
        return;
      }

      // Block GUI and in-game selection while a movie is playing
      if(GameState.VideoManager.isMoviePlaying() && event.button == 0){
        if(this.isGameCanvasMouseTarget(event)){
          GameState.VideoManager.skipMovie();
        }
        return;
      }

      Mouse.leftClick = true;

      if(Mouse.leftDown){
        if(!Mouse.pointerLock){
          Mouse.Update( event.clientX, event.clientY );
        }

        //If the NoClickTimer is active then we will return out of this function
        if(GameState.noClickTimer){
          return;
        }
        
        let clickCaptured = false;

        const customEvent = GUIControlEventFactory.generateEventObject();

        //Try to fire mouse up regardless if mouse is still inside object
        if(BitWise.InstanceOf(Mouse.downItem?.objectType, GUIControlTypeMask.GUIControl)){
          if(!(Mouse.downItem.widget.parent instanceof THREE.Scene)){
            try{
              Mouse.downItem.processEventListener('mouseUp', [customEvent]);
              Mouse.leftClick = false;
            }catch(e){
              console.error(e);
            }
          }
        }

        const uiControls = this.MenuGetActiveUIElements();
        for(let i = 0; i < uiControls.length; i++){
          if(!customEvent.propagate)
            break;

          const control = uiControls[i];
          if(control === Mouse.clickItem){
            if(typeof control.widget.parent !== 'undefined'){
              if(!(control.widget.parent instanceof THREE.Scene) && control.widget.visible){
                clickCaptured = true;
                try{
                  Mouse.clickItem = null;
                  //control.onClick(customEvent);
                  GameState.MenuManager.activeGUIElement = control;
                  control.processEventListener('click', [customEvent]);
                  GameState.guiAudioEmitter.playSoundFireAndForget('gui_click');
                  Mouse.leftClick = false;
                  EventManager.FireEvent('menu.click', {
                    name: control.name,
                    menu: control.menu.constructor.name
                  });
                }catch(e){
                  console.error(e);
                }
              }
            }
          }
        }

        let selectedObject = clickCaptured;
  
        if(!clickCaptured && (GameState.Mode != EngineMode.DIALOG)){
          if(GameState.Mode == EngineMode.INGAME && GameState.MenuManager.GetCurrentMenu() == GameState.MenuManager.InGameOverlay){
            const moduleObject = GameState.CursorManager.onMouseHitInteractive();
            if(BitWise.InstanceOf(moduleObject?.objectType, ModuleObjectType.ModuleObject)){
              if(moduleObject.isUseable() && moduleObject != GameState.getCurrentPlayer()){

                selectedObject = true;

                const distance = GameState.getCurrentPlayer().position.distanceTo(moduleObject.position);
                const distanceThreshold = 20;

                if(GameState.CursorManager.selectedObject == moduleObject && distance <= distanceThreshold){
                  if(typeof moduleObject.onClick === 'function'){
                    GameState.getCurrentPlayer().clearAllActions();
                    moduleObject.onClick(GameState.getCurrentPlayer());
                  }else{
                    let distance = GameState.getCurrentPlayer().position.distanceTo(moduleObject.position);
                    //console.log(distance);
                    if(distance > 1.5){
                      GameState.getCurrentPlayer().clearAllActions();
                      moduleObject.clearAllActions();
                      GameState.getCurrentPlayer().actionDialogObject(moduleObject);
                    }
                  }
                }
                GameState.CursorManager.setReticleSelectedObject(moduleObject);
              }
              if(GameState.debug.SELECTED_OBJECT)
                console.log('Ingame Object', moduleObject);
            }else{
              if(GameState.debug.SELECTED_OBJECT)
                console.log('Object', moduleObject);
            }

            if(!selectedObject){
              // GameState.CursorManager.hovered = GameState.CursorManager.hoveredObject = GameState.CursorManager.selected = GameState.CursorManager.selectedObject = undefined;
            }
          }
        }
      }
      if(event.button == 0){
        Mouse.leftDown = false;
        Mouse.downItem = undefined;
        Mouse.clickItem = undefined;
      }else if(event.button == 1){
        Mouse.rightDown = false;
      }
    });

    document.body.addEventListener('wheel', (e: WheelEvent) => {
      if(!this.isGameCanvasMouseTarget(e)){
        return;
      }
      if(e.deltaY < 0){
        if(BitWise.InstanceOf(GameState.MenuManager.hoveredGUIElement?.objectType, GUIControlTypeMask.GUIListBox)){
          (GameState.MenuManager.hoveredGUIElement as GUIListBox).scrollUp();
        }else if(BitWise.InstanceOf(GameState.MenuManager.hoveredGUIElement?.objectType, GUIControlTypeMask.GUIScrollBar)){
          (GameState.MenuManager.hoveredGUIElement as GUIScrollBar).list.scrollUp();
        }
      }else{
        if(BitWise.InstanceOf(GameState.MenuManager.hoveredGUIElement?.objectType, GUIControlTypeMask.GUIListBox)){
          (GameState.MenuManager.hoveredGUIElement as GUIListBox).scrollDown();
        }else if(BitWise.InstanceOf(GameState.MenuManager.hoveredGUIElement?.objectType, GUIControlTypeMask.GUIScrollBar)){
          (GameState.MenuManager.hoveredGUIElement as GUIScrollBar).list.scrollDown();
        }
      }
    });

    GameState.canvas.addEventListener('contextmenu', (e: MouseEvent) => {
      e.preventDefault();
    });

  }

  /**
   * True when the event landed on the game canvas (or pointer lock owns look input).
   * React chrome overlaying the canvas uses other targets and must not drive game input.
   */
  isGameCanvasMouseTarget(event: Event): boolean {
    if(Mouse.pointerLock){
      return true;
    }
    return event.target === this.element;
  }

  initKeys(){
    KeyMapper.BindKeyboard(this.keyboard, GameState.iniConfig);
    KeyMapper.BindGamepad(this.gamePad);

    //W
    KeyMapper.Actions[KeyMapAction.ActionUp].setProcessor( (keymap) => {
      if(GameState.State == EngineState.PAUSED) return;
      if(!keymap.keyboardInput?.down) return;
      if(this.gamePadMovement) return;
      const followee = GameState.PartyManager.party[0];
      if(!followee) return;
      if(!followee.canMove()) return;

      followee.clearAllActions(true);
      followee.force = 1;
      followee.setFacing(Utility.NormalizeRadian(FollowerCamera.facing + Math.PI/2), false, TURN_SPEED_FAST);
      followee.controlled = true;
      GameState.scene_cursor_holder.visible = true;
      FollowerCamera.clearFocusObject();
    });

    //S
    KeyMapper.Actions[KeyMapAction.ActionDown].setProcessor( (keymap) => {
      if(GameState.State == EngineState.PAUSED) return;
      if(!keymap.keyboardInput?.down) return;
      if(this.gamePadMovement) return;
      const followee = GameState.PartyManager.party[0];
      if(!followee) return;
      if(!followee.canMove()) return;
      
      followee.clearAllActions(true);
      followee.force = 1;
      followee.setFacing(Utility.NormalizeRadian(FollowerCamera.facing - Math.PI/2), false, TURN_SPEED_FAST);
      followee.controlled = true;
      GameState.scene_cursor_holder.visible = true;
      FollowerCamera.clearFocusObject();
    });

    //Z
    KeyMapper.Actions[KeyMapAction.ActionLeft].setProcessor( (keymap) => {
      if(GameState.State == EngineState.PAUSED) return;
      if(!keymap.keyboardInput?.down) return;
      const followee = GameState.PartyManager.party[0];
      if(!followee) return;
      if(!followee.canMove()) return;
    });

    //C
    KeyMapper.Actions[KeyMapAction.ActionRight].setProcessor( (keymap) => {
      if(GameState.State == EngineState.PAUSED) return;
      if(!keymap.keyboardInput?.down) return;
      const followee = GameState.PartyManager.party[0];
      if(!followee) return;
      if(!followee.canMove()) return;
    });

    //A
    KeyMapper.Actions[KeyMapAction.CameraRotateLeft].setProcessor( (keymap) => {
      // if(GameState.State == EngineState.PAUSED) return;
      if(GameState.Mode != EngineMode.INGAME) return;
      if(
        (keymap.keyboardInput.down || 
        (keymap.gamepadInput as AnalogInput).value < 0)
      ){
        FollowerCamera.clearFocusObject();
        FollowerCamera.turning = true;
        if(this.gamePad.stick_r_x.value){
          GameState.scene_cursor_holder.visible = false;
          FollowerCamera.dir = -this.gamePad.stick_r_x.value;
        }else{
          FollowerCamera.dir = 1;
          GameState.scene_cursor_holder.visible = true;
        }
      }
    });

    //D
    KeyMapper.Actions[KeyMapAction.CameraRotateRight].setProcessor( (keymap) => {
      // if(GameState.State == EngineState.PAUSED) return;
      if(GameState.Mode != EngineMode.INGAME) return;
      if(
        (keymap.keyboardInput.down || 
        (keymap.gamepadInput as AnalogInput).value > 0)
      ){
        FollowerCamera.clearFocusObject();
        FollowerCamera.turning = true;
        if(this.gamePad.stick_r_x.value){
          GameState.scene_cursor_holder.visible = false;
          FollowerCamera.dir = -this.gamePad.stick_r_x.value;
        }else{
          FollowerCamera.dir = -1;
          GameState.scene_cursor_holder.visible = true;
        }
      }
    });

    //ChangeLeader
    KeyMapper.Actions[KeyMapAction.ChangeChar].setProcessor( (keymap) => {
      if(!keymap.keyboardInput?.pressed && !keymap.gamepadInput?.pressed) return;
      GameState.PartyManager.ShiftLeader();
    });

    KeyMapper.Actions[KeyMapAction.Freelook].setProcessor( (keymap) => {
      if(!keymap.keyboardInput?.pressed && !keymap.gamepadInput?.pressed) return;
      GameState.SetEngineMode(GameState.Mode == EngineMode.FREELOOK ? EngineMode.INGAME : EngineMode.FREELOOK);
    });

    KeyMapper.Actions[KeyMapAction.WALKMODIFY].setProcessor( (keymap) => {
      if(!keymap.keyboardInput?.pressed && !keymap.gamepadInput?.pressed) return;
      const pc = GameState.getCurrentPlayer();
      if(pc){
        pc.walk = !pc.walk;
      }
    });

    //Dialog1
    KeyMapper.Actions[KeyMapAction.Dialog1].setProcessor( (keymap) => {
      if(!keymap.keyboardInput?.pressed && !keymap.gamepadInput?.pressed) return;
      GameState.CutsceneManager.selectReplyAtIndex(0);
    });

    //Dialog2
    KeyMapper.Actions[KeyMapAction.Dialog2].setProcessor( (keymap) => {
      if(!keymap.keyboardInput?.pressed && !keymap.gamepadInput?.pressed) return;
      GameState.CutsceneManager.selectReplyAtIndex(1);
    });

    //Dialog3
    KeyMapper.Actions[KeyMapAction.Dialog3].setProcessor( (keymap) => {
      if(!keymap.keyboardInput?.pressed && !keymap.gamepadInput?.pressed) return;
      GameState.CutsceneManager.selectReplyAtIndex(2);
    });

    //Dialog4
    KeyMapper.Actions[KeyMapAction.Dialog4].setProcessor( (keymap) => {
      if(!keymap.keyboardInput?.pressed && !keymap.gamepadInput?.pressed) return;
      GameState.CutsceneManager.selectReplyAtIndex(3);
    });

    //Dialog5
    KeyMapper.Actions[KeyMapAction.Dialog5].setProcessor( (keymap) => {
      if(!keymap.keyboardInput?.pressed && !keymap.gamepadInput?.pressed) return;
      GameState.CutsceneManager.selectReplyAtIndex(4);
    });

    //Dialog6
    KeyMapper.Actions[KeyMapAction.Dialog6].setProcessor( (keymap) => {
      if(!keymap.keyboardInput?.pressed && !keymap.gamepadInput?.pressed) return;
      GameState.CutsceneManager.selectReplyAtIndex(5);
    });

    //Dialog7
    KeyMapper.Actions[KeyMapAction.Dialog7].setProcessor( (keymap) => {
      if(!keymap.keyboardInput?.pressed && !keymap.gamepadInput?.pressed) return;
      GameState.CutsceneManager.selectReplyAtIndex(6);
    });

    //Dialog8
    KeyMapper.Actions[KeyMapAction.Dialog8].setProcessor( (keymap) => {
      if(!keymap.keyboardInput?.pressed && !keymap.gamepadInput?.pressed) return;
      GameState.CutsceneManager.selectReplyAtIndex(7);
    });

    //Dialog9
    KeyMapper.Actions[KeyMapAction.Dialog9].setProcessor( (keymap) => {
      if(!keymap.keyboardInput?.pressed && !keymap.gamepadInput?.pressed) return;
      GameState.CutsceneManager.selectReplyAtIndex(8);
    });

    //DialogSkip
    KeyMapper.Actions[KeyMapAction.DialogSkip].setProcessor( (keymap) => {
      if(!keymap.keyboardInput?.pressed && !keymap.gamepadInput?.pressed && !Mouse.leftClick) return;

      if(GameState.CutsceneManager.active && GameState.CutsceneManager.isListening){
        if(keymap.keyboardInput?.pressed || keymap.gamepadInput?.pressed){
          GameState.CutsceneManager.playerSkipEntry(GameState.CutsceneManager.currentEntry);
        }else if(Mouse.leftClick){
          GameState.CutsceneManager.playerSkipEntry(GameState.CutsceneManager.currentEntry);
        }
      }
    });

    //DialogAbort
    KeyMapper.Actions[KeyMapAction.DialogAbort].setProcessor( (keymap) => {
      if(!keymap.keyboardInput?.pressed && !keymap.gamepadInput?.pressed) return;

      if(GameState.CutsceneManager.active){
        GameState.CutsceneManager.endConversation(true);
      }
    })

    KeyMapper.Actions[KeyMapAction.SelectNext].setProcessor( (keymap) => {
      if(!keymap.keyboardInput?.pressed && !keymap.gamepadInput?.pressed) return;
      const nextObject = GameState.ModuleObjectManager.GetNextPlayerVisibleObject();
      if(nextObject){
        GameState.CursorManager.setReticleSelectedObject(nextObject);
        FollowerCamera.setFocusObject(nextObject);
      }
    });

    KeyMapper.Actions[KeyMapAction.SelectPrev].setProcessor( (keymap) => {
      if(!keymap.keyboardInput?.pressed && !keymap.gamepadInput?.pressed) return;
      const previousObject = GameState.ModuleObjectManager.GetPreviousPlayerVisibleObject();
      if(previousObject){
        GameState.CursorManager.setReticleSelectedObject(previousObject);
        FollowerCamera.setFocusObject(previousObject);
      }
    });

    KeyMapper.Actions[KeyMapAction.MGActionUp].setProcessor( (keymap, delta = 0) => {
      if(!keymap.keyboardInput?.down && !keymap.gamepadInput?.pressed) return;
      if(GameState.State != EngineState.RUNNING) return;
      switch(GameState.module.area.miniGame.type){
        case MiniGameType.SWOOPRACE:

        break;
        case MiniGameType.TURRET:
          GameState.module.area.miniGame.player.rotate('x', 1 * delta);
        break;
      }
    });

    KeyMapper.Actions[KeyMapAction.MGActionDown].setProcessor( (keymap, delta = 0) => {
      if(!keymap.keyboardInput?.down && !keymap.gamepadInput?.pressed) return;
      if(GameState.State != EngineState.RUNNING) return;
      switch(GameState.module.area.miniGame.type){
        case MiniGameType.SWOOPRACE:
        
        break;
        case MiniGameType.TURRET:
          GameState.module.area.miniGame.player.rotate('x', -1 * delta);
        break;
      }
    });

    KeyMapper.Actions[KeyMapAction.MGActionLeft].setProcessor( (keymap, delta = 0) => {
      if(!keymap.keyboardInput?.down && !keymap.gamepadInput?.pressed) return;
      if(GameState.State != EngineState.RUNNING) return;
      switch(GameState.module.area.miniGame.type){
        case MiniGameType.SWOOPRACE:
          GameState.module.area.miniGame.player.lateralForce = -GameState.module.area.miniGame.player.accel_lateral_secs;
        break;
        case MiniGameType.TURRET:
          GameState.module.area.miniGame.player.rotate('z', 1 * delta);
        break;
      }
    });

    KeyMapper.Actions[KeyMapAction.MGActionRight].setProcessor( (keymap, delta = 0) => {
      if(!keymap.keyboardInput?.down && !keymap.gamepadInput?.pressed) return;
      if(GameState.State != EngineState.RUNNING) return;
      switch(GameState.module.area.miniGame.type){
        case MiniGameType.SWOOPRACE:
          GameState.module.area.miniGame.player.lateralForce = GameState.module.area.miniGame.player.accel_lateral_secs;
        break;
        case MiniGameType.TURRET:
          GameState.module.area.miniGame.player.rotate('z', -1 * delta);
        break;
      }
    });

    KeyMapper.Actions[KeyMapAction.PauseMinigame].setProcessor( (keymap) => {
      if(!keymap.keyboardInput?.pressed && !keymap.gamepadInput?.pressed) return;
      GameState.State = ( GameState.State == EngineState.PAUSED ? EngineState.RUNNING : EngineState.PAUSED );
    });

    KeyMapper.Actions[KeyMapAction.MGshoot].setProcessor( (keymap) => {
      if(!keymap.keyboardInput?.pressed && !keymap.gamepadInput?.pressed) return;
      if(GameState.State != EngineState.RUNNING) return;
      switch(GameState.module.area.miniGame.type){
        case MiniGameType.TURRET:
          GameState.module.area.miniGame.player.fire();
        break;
        case MiniGameType.SWOOPRACE:
          GameState.module.area.miniGame.player.jump();
        break;
      }
    });

    KeyMapper.Actions[KeyMapAction.GUI].setProcessor( (keymap) => {
      if(!keymap.keyboardInput?.pressed && !keymap.gamepadInput?.pressed) return;
      const currentMenu = GameState.MenuManager.GetCurrentMenu();
      switch(GameState.Mode){
        case EngineMode.GUI:
          if(currentMenu.userCanClose){
            currentMenu.close();
          }
        break;
        default:
          GameState.MenuManager.MenuOptions.open();
        break;
      }
    });

    KeyMapper.Actions[KeyMapAction.Pause1].setProcessor( (keymap) => {
      if(!keymap.keyboardInput?.pressed && !keymap.gamepadInput?.pressed) return;

      const currentMenu = GameState.MenuManager.GetCurrentMenu();
      if(currentMenu == GameState.MenuManager.InGameOverlay){
        if(GameState.State == EngineState.RUNNING){
          GameState.AutoPauseManager.SignalAutoPauseEvent(0);
        }else{
          GameState.AutoPauseManager.Unpause();
        }
      }else if( currentMenu == GameState.MenuManager.InGameConfirm){
        GameState.MenuManager.InGameConfirm.close();
      }
    });

    KeyMapper.Actions[KeyMapAction.Flourish].setProcessor( (keymap) => {
      if(!keymap.keyboardInput?.pressed && !keymap.gamepadInput?.pressed) return;
      GameState.getCurrentPlayer().flourish();
    });

    KeyMapper.Actions[KeyMapAction.FlyUp].setProcessor( (keymap, delta = 0) => {
      if(!keymap.keyboardInput?.down && !keymap.gamepadInput?.pressed) return;
      const followee = GameState.PartyManager.party[0];
      if(!followee) return;
      followee.position.z += 5 * delta;
    });

    KeyMapper.Actions[KeyMapAction.FlyDown].setProcessor( (keymap, delta = 0) => {
      if(!keymap.keyboardInput?.down && !keymap.gamepadInput?.pressed) return;
      const followee = GameState.PartyManager.party[0];
      if(!followee) return;
      followee.position.z -= 5 * delta;
    });

    KeyMapper.Actions[KeyMapAction.ResolutionScaleUp].setProcessor( (keymap, delta = 0) => {
      if(!keymap.keyboardInput?.down && !keymap.gamepadInput?.pressed) return;
       GameState.rendererUpscaleFactor += 0.25;
      if(GameState.rendererUpscaleFactor >= 4) GameState.rendererUpscaleFactor = 4;
      GameState.updateRendererUpscaleFactor();
    });

    KeyMapper.Actions[KeyMapAction.ResolutionScaleDown].setProcessor( (keymap, delta = 0) => {
      if(!keymap.keyboardInput?.down && !keymap.gamepadInput?.pressed) return;
      GameState.rendererUpscaleFactor -= 0.25;
      if(GameState.rendererUpscaleFactor <= 0.25) GameState.rendererUpscaleFactor = 0.25;
      GameState.updateRendererUpscaleFactor();
    });

    KeyMapper.Actions[KeyMapAction.ResolutionScaleReset].setProcessor( (keymap, delta = 0) => {
      if(!keymap.keyboardInput?.down && !keymap.gamepadInput?.pressed) return;
      GameState.rendererUpscaleFactor = 1.0;
      GameState.updateRendererUpscaleFactor();
    });

    KeyMapper.Actions[KeyMapAction.Equip].setProcessor( (keymap) => {
      if(!keymap.keyboardInput?.pressed && !keymap.gamepadInput?.pressed) return;
      if(GameState.Mode != EngineMode.INGAME) return;
      GameState.MenuManager.MenuEquipment.open();
    });

    KeyMapper.Actions[KeyMapAction.Inventory].setProcessor( (keymap) => {
      if(!keymap.keyboardInput?.pressed && !keymap.gamepadInput?.pressed) return;
      if(GameState.Mode != EngineMode.INGAME) return;
      GameState.MenuManager.MenuInventory.open();
    });
    
    KeyMapper.Actions[KeyMapAction.Character].setProcessor( (keymap) => {
      if(!keymap.keyboardInput?.pressed && !keymap.gamepadInput?.pressed) return;
      if(GameState.Mode != EngineMode.INGAME) return;
      GameState.MenuManager.MenuCharacter.open();
    });
    
    KeyMapper.Actions[KeyMapAction.SkillsAndFeats].setProcessor( (keymap) => {
      if(!keymap.keyboardInput?.pressed && !keymap.gamepadInput?.pressed) return;
      if(GameState.Mode != EngineMode.INGAME) return;
      GameState.MenuManager.MenuAbilities.open();
    });
    
    
    KeyMapper.Actions[KeyMapAction.Messages].setProcessor( (keymap) => {
      if(!keymap.keyboardInput?.pressed && !keymap.gamepadInput?.pressed) return;
      if(GameState.Mode != EngineMode.INGAME) return;
      GameState.MenuManager.MenuMessages.open();
    });
    
    
    KeyMapper.Actions[KeyMapAction.Quests].setProcessor( (keymap) => {
      if(!keymap.keyboardInput?.pressed && !keymap.gamepadInput?.pressed) return;
      if(GameState.Mode != EngineMode.INGAME) return;
      GameState.MenuManager.MenuJournal.open();
    });
    
    
    KeyMapper.Actions[KeyMapAction.Map].setProcessor( (keymap) => {
      if(!keymap.keyboardInput?.pressed && !keymap.gamepadInput?.pressed) return;
      if(GameState.Mode != EngineMode.INGAME) return;
      GameState.MenuManager.MenuMap.open();
    });
    
    KeyMapper.Actions[KeyMapAction.Options].setProcessor( (keymap) => {
      if(!keymap.keyboardInput?.pressed && !keymap.gamepadInput?.pressed) return;
      if(GameState.Mode != EngineMode.INGAME) return;
      GameState.MenuManager.MenuOptions.open();
    });

      // Handle movie skipping
    KeyMapper.Actions[KeyMapAction.MovieSkip].setProcessor( (keymap) => {
      if(!keymap.keyboardInput?.pressed && !keymap.gamepadInput?.pressed) return;

      if(GameState.VideoManager.isMoviePlaying()){
        GameState.VideoManager.skipMovie();
      }
    });
  }

  MenuGetActiveUIElements(): GUIControl[] {
    let elements: GUIControl[] = [];

    for(let i = 0, len = GameState.MenuManager.activeModals.length; i < len; i++){
      const activeMenu = GameState.MenuManager.activeModals[i];
      if(!activeMenu.isVisible()) continue;
      
      elements = elements.concat(activeMenu.getActiveControls());
    }

    if(GameState.MenuManager.activeModals.length) return elements;

    for(let i = 0, len = GameState.MenuManager.activeMenus.length; i < len; i++){
      const activeMenu = GameState.MenuManager.activeMenus[i];
      if(!activeMenu.isVisible()) continue;

      elements = elements.concat(activeMenu.getActiveControls());
    }

    if(GameState.State == EngineState.PAUSED){
      elements = elements.concat(GameState.MenuManager.InGamePause.getActiveControls());
    }

    return elements.reverse();
  }

  Update(delta: number = 0){

    let xoffset = 0;
    let yoffset = 0;
    let currentMenu = GameState.MenuManager.GetCurrentMenu();

    this.gamePadMovement = false;

    let gp = undefined;
    if(GamePad.CurrentGamePad instanceof Gamepad){
      gp = navigator.getGamepads()[GamePad.CurrentGamePad.index];
      this.gamePad.setGamePad(gp);
      KeyMapper.BindGamepad(this.gamePad);
    }
    this.gamePad.updateState(delta);

    if(currentMenu){
      if(this.gamePad.button_a.pressed){
        currentMenu.triggerControllerAPress();
      }else if(this.gamePad.button_b.pressed){
        currentMenu.triggerControllerBPress();
      }else if(this.gamePad.button_x.pressed){
        currentMenu.triggerControllerXPress();
      }else if(this.gamePad.button_y.pressed){
        currentMenu.triggerControllerYPress();
      }else if(this.gamePad.button_bumper_l.pressed){
        currentMenu.triggerControllerBumperLPress();
      }else if(this.gamePad.button_bumper_r.pressed){
        currentMenu.triggerControllerBumperRPress();
      }else if(this.gamePad.button_d_up.pressed){
        currentMenu.triggerControllerDUpPress();
      }else if(this.gamePad.button_d_down.pressed){
        currentMenu.triggerControllerDDownPress();
      }else if(this.gamePad.button_d_left.pressed){
        currentMenu.triggerControllerDLeftPress();
      }else if(this.gamePad.button_d_right.pressed){
        currentMenu.triggerControllerDRightPress();
      }else if(this.gamePad.stick_l_x.pressed){
        currentMenu.triggerControllerLStickXPress( this.gamePad.stick_l_x.value > 0 ? true : false );
      }else if(this.gamePad.stick_l_y.pressed){
        currentMenu.triggerControllerLStickYPress( this.gamePad.stick_l_y.value > 0 ? true : false );
      }else if(this.gamePad.stick_r_x.pressed){
        currentMenu.triggerControllerRStickXPress( this.gamePad.stick_r_x.value > 0 ? true : false );
      }else if(this.gamePad.stick_r_y.pressed){
        currentMenu.triggerControllerRStickYPress( this.gamePad.stick_r_y.value > 0 ? true : false );
      }
    }

    if(GameState.State == EngineState.RUNNING){

      if(
        (GameState.Mode == EngineMode.INGAME) && 
        currentMenu != GameState.MenuManager.InGameConfirm && 
        currentMenu != GameState.MenuManager.MenuContainer
      ){
        const followee = GameState.PartyManager.party[0];
        if(followee){
          if(followee.canMove()){
            if( this.gamePad.stick_l_x.value || this.gamePad.stick_l_y.value ){
              followee.clearAllActions(true);
              followee.force = 1;
              followee.setFacing( Utility.NormalizeRadian( Math.atan2(-this.gamePad.stick_l_x.value, -this.gamePad.stick_l_y.value) + FollowerCamera.facing + Math.PI/2 ) , false, TURN_SPEED_FAST);
              followee.controlled = true;
              GameState.scene_cursor_holder.visible = false;
              this.gamePadMovement = true;
            }
          }
        }
      }
    }
    
    KeyMapper.ProcessMappings(GameState.Mode, delta);

    //Keyboard: onFrameEnd
    this.keyboard.onFrameEnd(delta);

    //Mouse: onFrameEnd
    Mouse.leftClick = false;

    Mouse.OldMouseX = Mouse.MouseX;
    Mouse.OldMouseY = Mouse.MouseY;
  }

  plChangeCallback(e: any){
    if(document.pointerLockElement === this.element) {
      Mouse.pointerLock = true;
    } else {
      Mouse.pointerLock = false;
    }
  }

}
